import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Settings → Alerte. Mirrors backend/src/routes/systemAlerts.ts (admins only).

export type SystemAlertSeverity = 'WARNING' | 'CRITICAL';

export interface SystemAlert {
  id: string;
  /** Stable machine key, e.g. "openai.credits". */
  key: string;
  /** "openai" | "anthropic" | "drive" | "anaf" | "smtp". */
  service: string;
  severity: SystemAlertSeverity;
  title: string;
  message: string;
  details: unknown;
  /** How many times the failure repeated while the alert was open. */
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: { id: string; name: string } | null;
  resolvedAt: string | null;
  /**
   * Anthropic / OpenAI alert while the AI comes with the Firmio subscription:
   * the provider account is Firmio's, so no link to it. Absent on older backends.
   */
  managedByFirmio?: boolean;
}

export interface SystemAlertsList {
  open: SystemAlert[];
  resolved: SystemAlert[];
}

export interface SystemAlertsSummary {
  open: number;
  unacknowledged: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getSystemAlerts(): Promise<SystemAlertsList> {
  const res = await apiFetch<Envelope<SystemAlertsList>>('/api/alerts', { headers: authHeaders() });
  return res.data;
}

export async function getSystemAlertsSummary(): Promise<SystemAlertsSummary> {
  const res = await apiFetch<Envelope<SystemAlertsSummary>>('/api/alerts/summary', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function acknowledgeSystemAlert(id: string): Promise<SystemAlertsList> {
  const res = await apiFetch<Envelope<SystemAlertsList>>(
    `/api/alerts/${encodeURIComponent(id)}/acknowledge`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export async function resolveSystemAlert(id: string): Promise<SystemAlertsList> {
  const res = await apiFetch<Envelope<SystemAlertsList>>(
    `/api/alerts/${encodeURIComponent(id)}/resolve`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}
